import { codeFont, numFont, plateFont } from '../../../utils/fonts';
import React from 'react';
import { Box, Chip, Typography } from '@mui/material';
import BoltIcon from '@mui/icons-material/Bolt';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import BlockIcon from '@mui/icons-material/Block';
import { formatPrice } from '../../../utils/vat';

/**
 * Small presentational pieces shared by the builder's result rows, cart and review
 * dialog. The price rule of the feature lives here: every purchase price is
 * VAT-exclusive and always carries the "alv 0 %" tag; nothing is shown untagged.
 *
 * The look follows Tarjousautomaatti (QuoteMachineDialog): white cards with a 14 px
 * radius on the near-white workspace, a navy icon tile in headings, saturated text on
 * a pale tint for tags, a segmented control whose chosen button is a white pill, and
 * a thin accent focus ring instead of the app's 2 px navy outline.
 */

// Tag colours: saturated text on a pale tint, as StatusBadge pairs its status colours.
export const TONE = {
  accent: { color: '#2b6cb0', bgcolor: '#ebf4ff', borderColor: '#bee3f8' },
  teal: { color: '#0f766e', bgcolor: '#ccfbf1', borderColor: '#99f6e4' },
  green: { color: '#2f8c54', bgcolor: '#e6ffed', borderColor: '#9ae6b4' },
  purple: { color: '#6b46c1', bgcolor: '#f5f0ff', borderColor: '#d6bcfa' },
  amber: { color: '#b45309', bgcolor: '#fef3e2', borderColor: '#fbd38d' },
  red: { color: '#c53030', bgcolor: '#fff5f5', borderColor: '#fed7d7' },
  slate: { color: '#475569', bgcolor: '#f1f5f9', borderColor: '#cbd5e1' },
};

export const CARD_SX = {
  bgcolor: 'background.paper',
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: '14px',
  overflow: 'hidden',
};

// A focused field inside the builder: a thin accent border with a soft ring.
export const FOCUS_RING_SX = {
  '& .MuiOutlinedInput-root.Mui-focused': { boxShadow: '0 0 0 3px rgba(49, 130, 206, 0.14)' },
  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'secondary.main', borderWidth: 1 },
};

// A segmented control: the group is the track, the chosen button a white pill on it.
export const SEGMENT_SX = {
  bgcolor: 'background.default',
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: '9px',
  p: '3px',
  gap: '2px',
  flexWrap: 'wrap',
  '& .MuiToggleButton-root': {
    border: 0,
    borderRadius: '7px !important',
    px: 1.25,
    py: 0.5,
    gap: 0.5,
    lineHeight: 1.4,
    fontSize: 12.5,
    fontWeight: 600,
    textTransform: 'none',
    color: 'text.secondary',
    whiteSpace: 'nowrap',
    '& .MuiSvgIcon-root': { fontSize: 15 },
    '&.Mui-selected': {
      bgcolor: 'background.paper',
      color: 'primary.main',
      boxShadow: '0 1px 2px rgba(0,0,0,.08)',
      '&:hover': { bgcolor: 'background.paper' },
    },
  },
};

// Compact inputs used inside cart rows and the destination select.
export const COMPACT_INPUT_SX = {
  '& .MuiOutlinedInput-root': { borderRadius: '9px', bgcolor: 'background.paper' },
  '& .MuiOutlinedInput-input': { py: 0.75, fontSize: 13 },
};

export function Overline({ children, sx }) {
  return (
    <Typography
      variant="overline"
      sx={{ display: 'block', fontSize: 11, fontWeight: 700, letterSpacing: '.08em', lineHeight: 1.4, color: 'text.secondary', ...sx }}
    >
      {children}
    </Typography>
  );
}

/** The navy icon tile Tarjousautomaatti puts next to its title. */
export function IconTile({ children, size = 40, tone, sx }) {
  const radius = size >= 36 ? '11px' : '8px';
  return (
    <Box
      sx={{
        width: size,
        height: size,
        borderRadius: radius,
        flexShrink: 0,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: tone ? tone.bgcolor : 'primary.main',
        color: tone ? tone.color : 'primary.contrastText',
        '& .MuiSvgIcon-root': { fontSize: Math.round(size * 0.55) },
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}

/** A 22 px tag: saturated text on a pale tint. */
export function TagChip({ label, tone = TONE.slate, icon, testId, sx }) {
  return (
    <Chip
      label={label}
      size="small"
      icon={icon}
      data-testid={testId}
      sx={{
        height: 22,
        fontSize: 12,
        fontWeight: 600,
        color: tone.color,
        bgcolor: tone.bgcolor,
        border: '1px solid',
        borderColor: tone.borderColor,
        '& .MuiChip-icon': { color: 'inherit', fontSize: 14, ml: '6px', mr: '-4px' },
        '& .MuiChip-label': { px: 1 },
        ...sx,
      }}
    />
  );
}

export function ExVatPrice({ value, t, size = 14, weight = 600, muted = false, testId }) {
  if (value == null || value === '') {
    return (
      <Typography component="span" sx={{ fontSize: size, color: 'text.disabled', ...numFont }} data-testid={testId}>
        –
      </Typography>
    );
  }
  return (
    <Typography
      component="span"
      sx={{ fontSize: size, fontWeight: weight, color: muted ? 'text.secondary' : 'text.primary', ...numFont, whiteSpace: 'nowrap' }}
      data-testid={testId}
    >
      {formatPrice(value)} €{' '}
      <Box component="span" sx={{ fontSize: Math.max(size - 3, 10), fontWeight: 500, color: 'text.secondary' }}>
        {t('orderBuilder.exVatTag')}
      </Box>
    </Typography>
  );
}

export function channelTone(integration) {
  if (!integration) return TONE.red;
  return integration.channel === 'api' ? TONE.accent : TONE.slate;
}

export function ChannelIcon({ integration, sx }) {
  if (!integration) return <BlockIcon sx={sx} />;
  return integration.channel === 'api' ? <BoltIcon sx={sx} /> : <EmailOutlinedIcon sx={sx} />;
}

export function ChannelChip({ integration, t }) {
  const isApi = integration?.channel === 'api';
  let label = t('orderBuilder.channel.none');
  if (integration) label = isApi ? t('orderBuilder.channel.api') : t('orderBuilder.channel.email');
  return (
    <TagChip
      label={label}
      tone={channelTone(integration)}
      icon={<ChannelIcon integration={integration} />}
      testId={integration ? `channel-chip-${integration.id}` : undefined}
    />
  );
}

/** Vendor group heading used by the results, the cart and the review: tile + name + channel. */
export function VendorHeading({ integration, name, t, aside, sx }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap', ...sx }}>
      <IconTile size={28} tone={channelTone(integration)}>
        <ChannelIcon integration={integration} />
      </IconTile>
      <Typography sx={{ fontSize: 15, fontWeight: 700, lineHeight: 1.3 }}>{name || integration?.display_name}</Typography>
      <ChannelChip integration={integration} t={t} />
      {aside && <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>{aside}</Box>}
    </Box>
  );
}

export function CodeText({ children, sx }) {
  return (
    <Typography component="span" sx={{ fontSize: 12.5, fontWeight: 500, color: 'text.secondary', ...codeFont, ...sx }}>
      {children}
    </Typography>
  );
}

/** "Varastoon" / "Työmääräys #123 · ABC-123" / "Lähete #17 · Tili Oy" / "Uusi lähete · Matti". */
export function destinationText(t, destination, { newLahete, inventoryEnabled } = {}) {
  const dest = destination || {};
  switch (dest.kind) {
    case 'maintenance':
      return dest.maintenance
        ? [t('orderBuilder.dest.workOrderNumber', { number: dest.maintenance.work_number }), dest.maintenance.registration_number]
            .filter(Boolean)
            .join(' · ')
        : t('orderBuilder.dest.workOrderUnset');
    case 'parts_sale':
      return dest.parts_sale
        ? [t('orderBuilder.laheteNumber', { number: dest.parts_sale.number }), dest.parts_sale.customer_name].filter(Boolean).join(' · ')
        : t('orderBuilder.dest.laheteUnset');
    case 'new_parts_sale':
      return newLahete?.customer
        ? `${t('orderBuilder.dest.newLahete')} · ${newLahete.customer.name || newLahete.customer.company_name || ''}`
        : t('orderBuilder.dest.newLaheteUnset');
    default:
      return inventoryEnabled ? t('orderBuilder.dest.stock') : t('orderBuilder.dest.noDestination');
  }
}

export function PlateText({ children }) {
  return (
    <Typography component="span" sx={{ fontSize: 12, fontWeight: 600, ...plateFont }}>
      {children}
    </Typography>
  );
}
